import { CssProperty } from "./dtos";

export interface ColorIntf {
    r: number; // 0-255
    g: number; // 0-255
    b: number; // 0-255
    a: number; // 0-1
}

export const parseColor = (color: string): ColorIntf => {
    // 1. Hex (#rgb, #rrggbb, #rgba, #rrggbbaa)
    if (color.startsWith('#')) {
        let hex = color.slice(1);
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        if (hex.length === 4) hex = hex.split('').map(c => c + c).join('');
        return {
            r: parseInt(hex.slice(0, 2), 16),
            g: parseInt(hex.slice(2, 4), 16),
            b: parseInt(hex.slice(4, 6), 16),
            a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1
        };
    }

    // 2. RGB/RGBA: Matches rgb(255, 255, 255) OR rgba(255, 255, 255, 0.5)
    // Now uses optional non-capturing group for the alpha channel
    const rgbMatch = color.match(/rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)|rgba\(\s*(\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\s*\)/);
    if (rgbMatch) {
        return {
            r: parseInt(rgbMatch[1] || rgbMatch[4]),
            g: parseInt(rgbMatch[2] || rgbMatch[5]),
            b: parseInt(rgbMatch[3] || rgbMatch[6]),
            a: rgbMatch[7] ? parseFloat(rgbMatch[7]) : 1
        };
    }

    // 3. HSL/HSLA: Matches hsl(360, 100%, 100%) OR hsla(360, 100%, 100%, 0.5)
    const hslMatch = color.match(/hsl\(\s*(\d+),\s*(\d+)%,\s*(\d+)%\s*\)|hsla\(\s*(\d+),\s*(\d+)%,\s*(\d+)%,\s*([\d.]+)\s*\)/);
    if (hslMatch) {
        const h = parseInt(hslMatch[1] || hslMatch[4]) / 360;
        const s = parseInt(hslMatch[2] || hslMatch[5]) / 100;
        const l = parseInt(hslMatch[3] || hslMatch[6]) / 100;
        const a = hslMatch[7] ? parseFloat(hslMatch[7]) : 1;
        
        // Conversion logic (same as before)
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        const hueToRgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1; if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        return {
            r: Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
            g: Math.round(hueToRgb(p, q, h) * 255),
            b: Math.round(hueToRgb(p, q, h - 1 / 3) * 255),
            a: a
        };
    }
    return { r: 0, g: 0, b: 0, a: 1 };
};

export function normalizeColor(input: string | null): string | null {
  if (!input) return null;

  const color = input.trim().toLowerCase();

  // 1. Already hex (#fff or #ffffff)
  if (color.startsWith("#")) {
    if (color.length === 4) {
      // #abc → #aabbcc
      return (
        "#" +
        color[1] + color[1] +
        color[2] + color[2] +
        color[3] + color[3]
      );
    }
    return color;
  }

  // 2. rgb() or rgba()
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    const [_, r, g, b] = rgbMatch;
    return (
      "#" +
      [r, g, b]
        .map((x) => parseInt(x).toString(16).padStart(2, "0"))
        .join("")
    );
  }

  // 3. hsl() or hsla()
  const hslMatch = color.match(/hsla?\((\d+),\s*(\d+)%?,\s*(\d+)%?/);
  if (hslMatch) {
    const [_, h, s, l] = hslMatch.map(Number);
    return hslToHex(h, s, l);
  }

  // 4. Named colors (rare but possible)
  const temp = document.createElement("div");
  temp.style.color = color;
  document.body.appendChild(temp);
  const computed = getComputedStyle(temp).color;
  temp.remove();

  const namedMatch = computed.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (namedMatch) {
    const [_, r, g, b] = namedMatch;
    return (
      "#" +
      [r, g, b]
        .map((x) => parseInt(x).toString(16).padStart(2, "0"))
        .join("")
    );
  }

  return null;
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  return (
    "#" +
    [f(0), f(8), f(4)]
      .map((x) => Math.round(x * 255).toString(16).padStart(2, "0"))
      .join("")
  );
}

export const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
    let r = 0, g = 0, b = 0;
    hex = hex.replace('#', '');
    if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
    } else {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
    }
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

// Source: W3C WAI Guidelines for Relative Luminance
export const isColorDark = (hex: string): boolean => {
    const rgb = parseInt(hex.replace('#', ''), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    
    // Calculate luminance using the W3C formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance < 0.5; // Returns true if the color is "dark"
};

const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s, l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    } else { s = 0; }
    return { h: h * 360, s: s * 100, l: l * 100 };
};

// Helper: HSL to RGB
const hslToRgb = (h: number, s: number, l: number) => {
    s /= 100; l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
    return { r: f(0) * 255, g: f(8) * 255, b: f(4) * 255 };
};

/**
 * Applies a 'warmth' factor (0-100) to a hex color.
 * 0 = Neutral (original color), 100 = Max warmth (pushed towards orange/red).
 */
export const applyWarmth = (color: ColorIntf, warmth: number): ColorIntf => {
    // 1. Convert ColorIntf (RGB) to HSL
    // (Assuming you have an rgbToHsl helper, otherwise standard math applies)
    let { h, s, l } = rgbToHsl(color.r, color.g, color.b);

    // 2. Apply Warmth Logic
    const warmthIntensity = warmth / 100;
    
    // Shift Hue towards 30 (Orange/Warm)
    h = h + (30 - h) * warmthIntensity;
    
    // Increase Saturation
    s = Math.min(100, s + (20 * warmthIntensity));

    // 3. Convert HSL back to RGB
    const { r, g, b } = hslToRgb(h, s, l);

    // 4. Return new ColorIntf, preserving original alpha
    return {
        r: Math.round(r),
        g: Math.round(g),
        b: Math.round(b),
        a: color.a
    };
};

/**
 * Generates a full surface scale, including the background (--surface-0),
 * relative to a base color and a pivot point (0-9).
 * * @param baseColor - The user-selected color
 * @param mode - 'light' or 'dark'
 * @param pivotIndex - The position (0-9) where baseColor should reside
 */
export const generateSurfaceScale = (
    baseColor: ColorIntf,
    mode: 'light' | 'dark',
    pivotIndex: number
): CssProperty[] => {
    const { h, s, l: baseL } = rgbToHsl(baseColor.r, baseColor.g, baseColor.b);
    const surfaceKeys = [0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
    
    // Light: 95% -> 5% | Dark: 5% -> 95%
    const startL = mode === 'light' ? 95 : 5;
    const endL = mode === 'light' ? 5 : 95;

    const result = surfaceKeys.map((key, index) => {
        let newL;
        if (index === pivotIndex) {
            newL = baseL;
        } else if (index < pivotIndex) {
            const t = pivotIndex === 0 ? 0 : index / pivotIndex;
            newL = startL + (baseL - startL) * t;
        } else {
            const t = (index - pivotIndex) / (surfaceKeys.length - 1 - pivotIndex);
            newL = baseL + (endL - baseL) * t;
        }

        return {
            property: `--surface-${key}`,
            value: `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(newL)}%)`
        };
    });

    if (pivotIndex === 0) {
        // console.table(result);
    }

    return result;
};

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export interface CSSProperty {
    key: string;
    value: string;
}

export interface NodePathItem {
    name: string;
    pos: number;
    depth: number;
    attributes: any;
}

export const parseStyles = (styleString?: string): CSSProperty[] => {
    if (!styleString) return [];
    return styleString.split(';')
        .filter(s => s.includes(':'))
        .map(s => {
            const [k, v] = s.split(':');
            return { key: k.trim(), value: v.trim() };
        });
};

export const formatStyles = (properties: CSSProperty[]): string => {
    return properties
        .filter(p => p.key.trim() !== '')
        .map(p => `${p.key}: ${p.value}`)
        .join('; ');
};

export const SAFE_CORE_CSS = [
    // Typography
    'color', 'font-family', 'font-size', 'font-weight', 'font-style', 
    'line-height', 'text-align', 'text-decoration', 'text-transform', 
    'letter-spacing', 'word-spacing', 'white-space',
    
    // Spacing & Sizing
    'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
    'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
    'width', 'min-width', 'max-width', 
    'height', 'min-height', 'max-height',
    
    // Borders & Shape
    'border', 'border-width', 'border-style', 'border-color',
    'border-top', 'border-right', 'border-bottom', 'border-left',
    'border-radius', 'outline', 'box-shadow',
    
    // Backgrounds
    'background', 'background-color', 'background-image', 'background-size', 
    'background-repeat', 'background-position', 'opacity',
    
    // Flex Layout (Safe for containers)
    'display', 'flex-direction', 'justify-content', 'align-items', 'gap',
    
    // Miscellaneous
    'cursor', 'overflow', 'vertical-align', 'visibility'
];

export const VALUE_SUGGESTIONS: Record<string, string[]> = {
    'display': ['block', 'inline', 'flex', 'grid', 'inline-block', 'none'],
    'text-align': ['left', 'center', 'right', 'justify'],
    'font-weight': ['normal', 'bold', '100', '400', '700', '900'],
    'font-style': ['normal', 'italic'],
    'font-family': ['var(--font-family)', 'inherit', 'monospace'],
    'border-style': ['solid', 'dashed', 'dotted', 'double', 'none'],
    'cursor': ['pointer', 'default', 'not-allowed', 'wait', 'move'],
    'vertical-align': ['top', 'middle', 'bottom', 'baseline'],
    'white-space': ['normal', 'nowrap', 'pre', 'pre-wrap'],
    'visibility': ['visible', 'hidden', 'collapse'],
    'flex-direction': ['row', 'column', 'row-reverse', 'column-reverse'],
    'justify-content': ['flex-start', 'center', 'flex-end', 'space-between', 'space-around'],
    'align-items': ['flex-start', 'center', 'flex-end', 'stretch', 'baseline'],
};

// Generic units for numeric fields
export const UNIT_SUGGESTIONS = ['0', 'auto', '10px', '20px', '1rem', '2rem', '50%', '100%'];

export const BASIC_COLORS = [
    'transparent', 'aliceblue', 'antiquewhite', 'aqua', 'aquamarine', 'azure', 'beige', 'bisque', 'black', 'blanchedalmond', 
    'blue', 'blueviolet', 'brown', 'burlywood', 'cadetblue', 'chartreuse', 'chocolate', 'coral', 'cornflowerblue', 'cornsilk', 
    'crimson', 'cyan', 'darkblue', 'darkcyan', 'darkgoldenrod', 'darkgray', 'darkgreen', 'darkgrey', 'darkkhaki', 'darkmagenta', 
    'darkolivegreen', 'darkorange', 'darkorchid', 'darkred', 'darksalmon', 'darkseagreen', 'darkslateblue', 'darkslategray', 
    'darkslategrey', 'darkturquoise', 'darkviolet', 'deeppink', 'deepskyblue', 'dimgray', 'dimgrey', 'dodgerblue', 'firebrick', 
    'floralwhite', 'forestgreen', 'fuchsia', 'gainsboro', 'ghostwhite', 'gold', 'goldenrod', 'gray', 'green', 'greenyellow', 
    'grey', 'honeydew', 'hotpink', 'indianred', 'indigo', 'ivory', 'khaki', 'lavender', 'lavenderblush', 'lawngreen', 
    'lemonchiffon', 'lightblue', 'lightcoral', 'lightcyan', 'lightgoldenrodyellow', 'lightgray', 'lightgreen', 'lightgrey', 
    'lightpink', 'lightsalmon', 'lightseagreen', 'lightskyblue', 'lightslategray', 'lightslategrey', 'lightsteelblue', 
    'lightyellow', 'lime', 'limegreen', 'linen', 'magenta', 'maroon', 'mediumaquamarine', 'mediumblue', 'mediumorchid', 
    'mediumpurple', 'mediumseagreen', 'mediumslateblue', 'mediumspringgreen', 'mediumturquoise', 'mediumvioletred', 
    'midnightblue', 'mintcream', 'mistyrose', 'moccasin', 'navajowhite', 'navy', 'oldlace', 'olive', 'olivedrab', 'orange', 
    'orangered', 'orchid', 'palegoldenrod', 'palegreen', 'paleturquoise', 'palevioletred', 'papayawhip', 'peachpuff', 'peru', 
    'pink', 'plum', 'powderblue', 'purple', 'rebeccapurple', 'red', 'rosybrown', 'royalblue', 'saddlebrown', 'salmon', 
    'sandybrown', 'seagreen', 'seashell', 'sienna', 'silver', 'skyblue', 'slateblue', 'slategray', 'slategrey', 'snow', 
    'springgreen', 'steelblue', 'tan', 'teal', 'thistle', 'tomato', 'turquoise', 'violet', 'wheat', 'white', 'whitesmoke', 
    'yellow', 'yellowgreen'
];

export const PRIMEREACT_THEME_VARS = [
    // Backgrounds & Surfaces
    '--primary-color', '--primary-color-text',
    '--surface-ground', '--surface-section', '--surface-card', '--surface-overlay', '--surface-border', '--surface-hover',
    '--surface-0', '--surface-50', '--surface-100', '--surface-200', '--surface-300', '--surface-400', 
    '--surface-500', '--surface-600', '--surface-700', '--surface-800', '--surface-900',
    // Text Colors
    '--text-color', '--text-color-secondary',
    // Functional
    '--maskbg', '--highlight-bg', '--highlight-text-color'
];

// Helper to check if a property should suggest colors
export const isColorProperty = (key: string) => {
    const k: string = key?.toLowerCase() || '';
    return k.includes('color') || k === 'background' || k === 'border' || k === 'outline';
};

export const isColor = (property: string, value: string): boolean => {
    // 1. Keep your existing name-based check (e.g., color, background-color, etc.)
    if (isColorProperty(property)) return true;

    // 2. Add hex color validation
    const hexRegex = /^#([A-Fa-f0-9]{3}){1,2}$/;
    if (hexRegex.test(value)) return true;

    // 3. Add rgb/rgba/hsl validation if needed
    if (value.startsWith('rgb') || value.startsWith('hsl')) return true;

    // 4. Handle var() references (if you want to force previewing them)
    if (value.startsWith('var(')) return true;

    return false;
};

export const extractColorFromValue = (value: string): string | null => {
    // Matches hex, rgb, rgba, hsl, hsla
    const colorRegex = /(#[\da-fA-F]{3,8}|rgba?\([\d\s,.]+\)|hsla?\([\d\s,.]+\))/;
    const match = value.match(colorRegex);
    return match ? match[0] : null;
};

export interface FontResource {
    name: string;
    url: string;
}

const PLAYFUL_FONTS: FontResource[] = [
  { name: "Abril Fatface", url: "https://fonts.googleapis.com/css2?family=Abril+Fatface&display=swap" },
  { name: "Akronim", url: "https://fonts.googleapis.com/css2?family=Akronim&display=swap" },
  { name: "Aldrich", url: "https://fonts.googleapis.com/css2?family=Aldrich&display=swap" },
  { name: "Alfa Slab One", url: "https://fonts.googleapis.com/css2?family=Alfa+Slab+One&display=swap" },
  { name: "Atomic Age", url: "https://fonts.googleapis.com/css2?family=Atomic+Age&display=swap" },
  { name: "Audiowide", url: "https://fonts.googleapis.com/css2?family=Audiowide&display=swap" },
  { name: "Bangers", url: "https://fonts.googleapis.com/css2?family=Bangers&display=swap" },
  { name: "Black Ops One", url: "https://fonts.googleapis.com/css2?family=Black+Ops+One&display=swap" },
  { name: "Bowlby One SC", url: "https://fonts.googleapis.com/css2?family=Bowlby+One+SC&display=swap" },
  { name: "Bungee Shade", url: "https://fonts.googleapis.com/css2?family=Bungee+Shade&display=swap" },
  { name: "Caesar Dressing", url: "https://fonts.googleapis.com/css2?family=Caesar+Dressing&display=swap" },
  { name: "Chewy", url: "https://fonts.googleapis.com/css2?family=Chewy&display=swap" },
  { name: "Chilanka", url: "https://fonts.googleapis.com/css2?family=Chilanka&display=swap" },
  { name: "Coda", url: "https://fonts.googleapis.com/css2?family=Coda&display=swap" },
  { name: "Creepster", url: "https://fonts.googleapis.com/css2?family=Creepster&display=swap" },
  { name: "Eater", url: "https://fonts.googleapis.com/css2?family=Eater&display=swap" },
  { name: "Faster One", url: "https://fonts.googleapis.com/css2?family=Faster+One&display=swap" },
  { name: "Finger Paint", url: "https://fonts.googleapis.com/css2?family=Finger+Paint&display=swap" },
  { name: "Fontdiner Swanky", url: "https://fonts.googleapis.com/css2?family=Fontdiner+Swanky&display=swap" },
  { name: "Fredericka the Great", url: "https://fonts.googleapis.com/css2?family=Fredericka+the+Great&display=swap" },
  { name: "Frijole", url: "https://fonts.googleapis.com/css2?family=Frijole&display=swap" },
  { name: "Fugaz One", url: "https://fonts.googleapis.com/css2?family=Fugaz+One&display=swap" },
  { name: "Galada", url: "https://fonts.googleapis.com/css2?family=Galada&display=swap" },
  { name: "Geostar Fill", url: "https://fonts.googleapis.com/css2?family=Geostar+Fill&display=swap" },
  { name: "Germania One", url: "https://fonts.googleapis.com/css2?family=Germania+One&display=swap" },
  { name: "Grand Hotel", url: "https://fonts.googleapis.com/css2?family=Grand+Hotel&display=swap" },
  { name: "Gravitas One", url: "https://fonts.googleapis.com/css2?family=Gravitas+One&display=swap" },
  { name: "Gruppo", url: "https://fonts.googleapis.com/css2?family=Gruppo&display=swap" },
  { name: "Hanalei Fill", url: "https://fonts.googleapis.com/css2?family=Hanalei+Fill&display=swap" },
  { name: "Holtwood One SC", url: "https://fonts.googleapis.com/css2?family=Holtwood+One+SC&display=swap" },
  { name: "Homemade Apple", url: "https://fonts.googleapis.com/css2?family=Homemade+Apple&display=swap" },
  { name: "Iceberg", url: "https://fonts.googleapis.com/css2?family=Iceberg&display=swap" },
  { name: "Jolly Lodger", url: "https://fonts.googleapis.com/css2?family=Jolly+Lodger&display=swap" },
  { name: "Kablammo", url: "https://fonts.googleapis.com/css2?family=Kablammo&display=swap" },
  { name: "Kavoon", url: "https://fonts.googleapis.com/css2?family=Kavoon&display=swap" },
  { name: "Kumar One", url: "https://fonts.googleapis.com/css2?family=Kumar+One&display=swap" },
  { name: "Luckiest Guy", url: "https://fonts.googleapis.com/css2?family=Luckiest+Guy&display=swap" },
  { name: "Metal Mania", url: "https://fonts.googleapis.com/css2?family=Metal+Mania&display=swap" },
  { name: "Monoton", url: "https://fonts.googleapis.com/css2?family=Monoton&display=swap" },
  { name: "New Rocker", url: "https://fonts.googleapis.com/css2?family=New+Rocker&display=swap" },
  { name: "Nosifer", url: "https://fonts.googleapis.com/css2?family=Nosifer&display=swap" },
  { name: "Permanent Marker", url: "https://fonts.googleapis.com/css2?family=Permanent+Marker&display=swap" },
  { name: "Press Start 2P", url: "https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap" },
  { name: "Righteous", url: "https://fonts.googleapis.com/css2?family=Righteous&display=swap" },
  { name: "Rye", url: "https://fonts.googleapis.com/css2?family=Rye&display=swap" },
  { name: "Shojumaru", url: "https://fonts.googleapis.com/css2?family=Shojumaru&display=swap" },
  { name: "Sriracha", url: "https://fonts.googleapis.com/css2?family=Sriracha&display=swap" },
  { name: "Stardos Stencil", url: "https://fonts.googleapis.com/css2?family=Stardos+Stencil&display=swap" },
  { name: "Trade Winds", url: "https://fonts.googleapis.com/css2?family=Trade+Winds&display=swap" },
  { name: "UnifrakturCook:wght@700", url: "https://fonts.googleapis.com/css2?family=UnifrakturCook:wght@700&display=swap" }
];

const PROFESSIONAL_FONTS: FontResource[] = [
  { name: "Archivo", url: "https://fonts.googleapis.com/css2?family=Archivo:wght@400;700&display=swap" },
  { name: "Arimo", url: "https://fonts.googleapis.com/css2?family=Arimo:wght@400;700&display=swap" },
  { name: "Asap", url: "https://fonts.googleapis.com/css2?family=Asap:wght@400;700&display=swap" },
  { name: "Bebas Neue", url: "https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap" },
  { name: "Bitter", url: "https://fonts.googleapis.com/css2?family=Bitter:wght@400;700&display=swap" },
  { name: "Cabin", url: "https://fonts.googleapis.com/css2?family=Cabin:wght@400;700&display=swap" },
  { name: "Comfortaa", url: "https://fonts.googleapis.com/css2?family=Comfortaa:wght@400;700&display=swap" },
  { name: "DM Sans", url: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700&display=swap" },
  { name: "Dancing Script", url: "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap" },
  { name: "Exo 2", url: "https://fonts.googleapis.com/css2?family=Exo+2:wght@400;700&display=swap" },
  { name: "Fira Sans", url: "https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;700&display=swap" },
  { name: "Heebo", url: "https://fonts.googleapis.com/css2?family=Heebo:wght@400;700&display=swap" },
  { name: "Hind", url: "https://fonts.googleapis.com/css2?family=Hind:wght@400;700&display=swap" },
  { name: "IBM Plex Sans", url: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;700&display=swap" },
  { name: "Inconsolata", url: "https://fonts.googleapis.com/css2?family=Inconsolata:wght@400;700&display=swap" },
  { name: "Inter", url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap" },
  { name: "Josefin Sans", url: "https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@400;700&display=swap" },
  { name: "Karla", url: "https://fonts.googleapis.com/css2?family=Karla:wght@400;700&display=swap" },
  { name: "Lato", url: "https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap" },
  { name: "Lobster", url: "https://fonts.googleapis.com/css2?family=Lobster&display=swap" },
  { name: "Lora", url: "https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,700;1,400&display=swap" },
  { name: "Manrope", url: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;700&display=swap" },
  { name: "Maven Pro", url: "https://fonts.googleapis.com/css2?family=Maven+Pro:wght@400;700&display=swap" },
  { name: "Merriweather", url: "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,400;0,700;1,400&display=swap" },
  { name: "Montserrat", url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap" },
  { name: "Mukta", url: "https://fonts.googleapis.com/css2?family=Mukta:wght@400;700&display=swap" },
  { name: "Noto Sans", url: "https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;700&display=swap" },
  { name: "Nunito", url: "https://fonts.googleapis.com/css2?family=Nunito:wght@400;700&display=swap" },
  { name: "Open Sans", url: "https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap" },
  { name: "Oswald", url: "https://fonts.googleapis.com/css2?family=Oswald:wght@400;700&display=swap" },
  { name: "Outfit", url: "https://fonts.googleapis.com/css2?family=Outfit:wght@400;700&display=swap" },
  { name: "Oxygen", url: "https://fonts.googleapis.com/css2?family=Oxygen:wght@400;700&display=swap" },
  { name: "PT Sans", url: "https://fonts.googleapis.com/css2?family=PT+Sans:wght@400;700&display=swap" },
  { name: "Pacifico", url: "https://fonts.googleapis.com/css2?family=Pacifico&display=swap" },
  { name: "Playfair Display", url: "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&display=swap" },
  { name: "Poppins", url: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap" },
  { name: "Quicksand", url: "https://fonts.googleapis.com/css2?family=Quicksand:wght@400;700&display=swap" },
  { name: "Raleway", url: "https://fonts.googleapis.com/css2?family=Raleway:wght@400;700&display=swap" },
  { name: "Roboto", url: "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap" },
  { name: "Roboto Condensed", url: "https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700&display=swap" },
  { name: "Roboto Mono", url: "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;700&display=swap" },
  { name: "Rubik", url: "https://fonts.googleapis.com/css2?family=Rubik:wght@400;700&display=swap" },
  { name: "Shadows Into Light", url: "https://fonts.googleapis.com/css2?family=Shadows+Into+Light&display=swap" },
  { name: "Source Sans 3", url: "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;600;700&display=swap" },
  { name: "Space Grotesk", url: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;700&display=swap" },
  { name: "Sriracha", url: "https://fonts.googleapis.com/css2?family=Sriracha&display=swap" },
  { name: "Syne", url: "https://fonts.googleapis.com/css2?family=Syne:wght@400;700&display=swap" },
  { name: "Titillium Web", url: "https://fonts.googleapis.com/css2?family=Titillium+Web:wght@400;700&display=swap" },
  { name: "Ubuntu", url: "https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;700&display=swap" },
  { name: "Varela Round", url: "https://fonts.googleapis.com/css2?family=Varela+Round&display=swap" },
  { name: "Work Sans", url: "https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;700&display=swap" }
];

export const FONT_LIBRARY: FontResource[] = [...PROFESSIONAL_FONTS, ...PLAYFUL_FONTS].sort((a, b) => 
  a.name.localeCompare(b.name)
);

export const getFontFamilyName = (url: string): string => {
    try {
        const params = new URLSearchParams(url.split('?')[1]);
        const family = params.get('family');
        return family ? family.split(':')[0].replace(/\+/g, ' ') : 'Custom Font';
    } catch { return 'Custom Font'; }
};

export interface GradientStop {
    id: string;
    color: string;
    position: number;
}

export interface GradientConfig {
    type: 'linear' | 'radial';
    angle: number;
    stops: GradientStop[];
}

// Convert CSS string -> GradientConfig object
export const parseCssGradient = (css: string): GradientConfig => {
    // Basic parser: extracts type, angle (if linear), and stops
    const isLinear = css.includes('linear-gradient');
    const angleMatch = css.match(/(\d+)deg/);
    const angle = angleMatch ? parseInt(angleMatch[1]) : 180;
    
    // Regex to find all color-position pairs (e.g., #fff 0%)
    const stopMatches = Array.from(css.matchAll(/(#[a-fA-F0-9]{3,8}|rgba?\([^)]+\))\s+(\d+)%/g));
    
    const stops: GradientStop[] = stopMatches.map((m, i) => ({
        id: i.toString(),
        color: m[1],
        position: parseInt(m[2])
    }));

    return {
        type: isLinear ? 'linear' : 'radial',
        angle: angle,
        stops: stops.length > 0 ? stops : [{ id: '0', color: '#ffffff', position: 0 }, { id: '1', color: '#000000', position: 100 }]
    };
};

// Convert GradientConfig object -> CSS string
export const serializeGradient = (config: GradientConfig): string => {
    const stopsStr = config.stops
        .sort((a, b) => a.position - b.position)
        .map(s => `${s.color} ${s.position}%`)
        .join(', ');

    return config.type === 'linear' 
        ? `linear-gradient(${config.angle}deg, ${stopsStr})`
        : `radial-gradient(circle, ${stopsStr})`;
};

export const EUROPEAN_LANGUAGES: string[] = [
  "English",
  "Albanian",
  "Basque",
//   "Belarusian",
  "Bosnian",
  "Breton",
  "Bulgarian",
  "Catalan",
  "Corsican",
  "Croatian",
  "Czech",
  "Danish",
  "Dutch",
  "Estonian",
  "Faroese",
  "Finnish",
  "French",
  "Frisian",
  "Galician",
  "German",
  "Greek",
  "Hungarian",
  "Icelandic",
  "Irish",
  "Italian",
  "Latvian",
  "Lithuanian",
  "Luxembourgish",
  "Macedonian",
  "Maltese",
  "Norwegian",
  "Polish",
  "Portuguese",
  "Romanian",
//   "Russian",
  "Scottish Gaelic",
  "Serbian",
  "Slovak",
  "Slovenian",
  "Spanish",
  "Swedish",
  "Ukrainian",
  "Welsh"
];
